import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles, Clock, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";

const TrialContext = createContext(null);

export const TrialProvider = ({ children }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  const refresh = useCallback(() => {
    if (!user || user === false) return;
    api
      .get("/trial/status")
      .then(({ data }) => {
        localStorage.setItem("cx_trial", JSON.stringify(data));
        setStatus(data);
      })
      .catch((e) => {
        const cached = !e.response && localStorage.getItem("cx_trial");
        setStatus(cached ? JSON.parse(cached) : false);
      });
  }, [user]);

  useEffect(refresh, [refresh]);

  return <TrialContext.Provider value={{ status, refresh }}>{children}</TrialContext.Provider>;
};

export const useTrial = () => useContext(TrialContext) || { status: null, refresh: () => {} };

export const TrialBanner = () => {
  const { status } = useTrial();
  if (!status || status.state !== "trial") return null;
  const urgent = status.days_remaining <= 3;
  return (
    <div
      className={`flex flex-wrap items-center gap-2 px-3 py-2 border-b text-[12px] ${
        urgent ? "bg-red-500/10 border-red-500/40" : "bg-[#F97316]/10 border-[#F97316]/40"
      }`}
      data-testid="trial-banner"
    >
      <Clock className={`w-3.5 h-3.5 ${urgent ? "text-red-500" : "text-[#F97316]"}`} />
      <span data-testid="trial-banner-text">
        <strong>Trial: {status.days_remaining} {status.days_remaining === 1 ? "day" : "days"} remaining.</strong>{" "}
        Upgrade to unlock AI features.
      </span>
      <Link to="/pricing" className="ml-auto font-semibold text-[#F97316] hover:underline" data-testid="trial-banner-upgrade">
        Upgrade now →
      </Link>
    </div>
  );
};

export const TrialExpired = () => {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="trial-expired-page">
      <div className="w-full max-w-lg text-center">
        <Logo size="lg" className="justify-center" />
        <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/40 flex items-center justify-center mx-auto mt-8">
          <Lock className="w-6 h-6 text-red-500" />
        </div>
        <h1 className="font-display text-3xl font-semibold mt-5">Your trial has expired</h1>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
          The 14-day free trial for <strong>{user?.email}</strong> has ended, so the workspace is locked. Every project,
          DPR and quantity you logged is safely retained — upgrading restores access immediately.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mt-7 text-left">
          {[["Starter", "₹1,999 / month", "3 projects · 5 users"],
            ["Professional", "₹4,999 / month", "Unlimited projects · AI modules"]].map(([n, p, s]) => (
            <div key={n} className="border border-border bg-card p-3">
              <div className="font-semibold text-[13px]">{n}</div>
              <div className="num text-lg font-semibold text-[#F97316]">{p}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{s}</div>
            </div>
          ))}
        </div>
        <Link to="/pricing">
          <Button size="lg" className="w-full mt-6" data-testid="expired-upgrade-btn">
            Upgrade with Razorpay <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
        <button onClick={logout} className="text-[12px] text-muted-foreground hover:text-foreground mt-4" data-testid="expired-logout-btn">
          Sign out
        </button>
      </div>
    </div>
  );
};

export const AiLocked = ({ feature }) => (
  <div className="border border-[#F97316]/40 bg-[#F97316]/5 p-8 text-center" data-testid="ai-locked-panel">
    <div className="w-12 h-12 rounded-full bg-[#F97316]/15 flex items-center justify-center mx-auto">
      <Sparkles className="w-5 h-5 text-[#F97316]" />
    </div>
    <h2 className="font-display text-2xl font-semibold mt-4">{feature} is a paid feature</h2>
    <p className="text-sm text-muted-foreground mt-3 max-w-lg mx-auto leading-relaxed">
      AI modules — Risk Radar, the AI BOQ Parser and AI risk briefs — are not included in the free trial. Every
      planning and execution module stays open: WBS, DPR, tiered planning, CPM, hindrances, resources, EVM costing,
      store and KPIs.
    </p>
    <Link to="/pricing">
      <Button className="mt-6" data-testid="ai-locked-upgrade-btn">
        Unlock with Professional — ₹4,999 / month
      </Button>
    </Link>
  </div>
);
